import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { updateUserType } from '../utils/userType';
import styles from './HairstylistCard.module.css';
import instagramIcon from '../assets/images/logo_instagram.png';
import whatsappIcon from '../assets/images/logo_whatsapp.png';
import tiktokIcon from '../assets/images/tiktok_logo.png';
import linkIcon from '../assets/images/logo_personal_link.png';
import phoneIcon from '../assets/images/telephone_logo.png';
import anonymousAvatar from '../assets/images/anonymous-avatar.svg';
import { useReviews } from '../hooks/useReviews';
import { useFavorites } from '../hooks/useFavorites';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import AuthModal from './AuthModal';
import StarRating from './StarRating';
import { logFavoriteAction } from '../utils/analyticsEvents';
import { logContactClick } from '../utils/hairstylistAnalytics';
import { getCountryFlag } from '../utils/countryFlags';
import { getDisplayLocation, getCountry } from '../utils/locationHelpers';

function HairstylistCard({ 
  stylist, 
  serviceLookup = {}, 
  serviceCategoryLookup = {},
  isDraggable = false,
  dragIndex = 0,
  isDragged = false,
  isDragOver = false,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}) {
  const {
    id,
    name,
    country, // Code pays (fr, be, ch, ca)
    description,
    services = [],
    profilePhoto,
    contact,
    canTravel,
    galleryPhotos = [],
    accountType = 'PRO',
  } = stylist;

  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const { averageRating, reviewCount } = useReviews(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Vérifier si l'utilisateur est admin
  const displayName = currentUser?.displayName || currentUser?.email?.split('@')[0] || '';
  const isAdminUser = currentUser && displayName?.toLowerCase() === 'admin';
  const chipsContainerRef = useRef(null);
  const rightColumnMobileRef = useRef(null);
  const rightColumnDesktopRef = useRef(null);
  const [visibleCategoriesCount, setVisibleCategoriesCount] = useState(0);
  const [showMoreIndicator, setShowMoreIndicator] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const avatarSrc = profilePhoto || anonymousAvatar;

  const handleCardClick = () => {
    try {
      const scrollY = window.scrollY ?? window.pageYOffset ?? 0;
      sessionStorage.setItem('searchPageScrollY', String(scrollY));
    } catch (e) {
      // ignore storage errors
    }

    // Si on est sur la page de recherche, ouvrir la fiche en modale
    if (location.pathname.startsWith('/search')) {
      navigate(`/hairstylist/${id}`, {
        state: { background: location },
      });
    } else {
      // Ailleurs (profil, favoris, etc.), garder le comportement plein écran
      navigate(`/hairstylist/${id}`);
    }
  };

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }
    try {
      const wasFavorite = isFavorite(id);
      await toggleFavorite(id);
      // Log analytics
      logFavoriteAction(window.location.pathname, id, !wasFavorite);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error toggling favorite:', error);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!isAdminUser) return;
    
    const confirmed = window.confirm(
      `Êtes-vous sûr de vouloir supprimer le profil de ${name} ? Cette action est irréversible.`
    );
    
    if (!confirmed) return;
    
    setDeleting(true);
    try {
      // Get userId before deleting to update userType
      const userId = stylist.userId;
      
      // Delete the hairstylist profile
      await deleteDoc(doc(db, 'hairstylists', id));
      
      // Update userType to "client" if the profile had a userId
      if (userId) {
        try {
          await updateUserType(userId);
        } catch (userTypeError) {
          // eslint-disable-next-line no-console
          console.error('Error updating userType after profile deletion:', userTypeError);
          // Don't fail the deletion if userType update fails
        }
      }
      
      // Optionnel: recharger la page ou mettre à jour la liste
      window.location.reload();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error deleting hairstylist:', error);
      alert('Erreur lors de la suppression du profil.');
      setDeleting(false);
    }
  };

  // Services : collecter toutes les catégories uniques
  const categoryLabels = useMemo(() => {
    const labels = [];
    const seenCategories = new Set();
    services.forEach((code) => {
      const categoryInfo = serviceCategoryLookup[code];
      const label = categoryInfo?.label;
      const categoryKey = categoryInfo?.code || label || code;
      if (!label || seenCategories.has(categoryKey)) {
        return;
      }
      seenCategories.add(categoryKey);
      labels.push(label);
    });
    return labels;
  }, [services, serviceCategoryLookup]);

  // Calculer dynamiquement combien de catégories peuvent tenir dans le container
  useEffect(() => {
    if (!chipsContainerRef.current || categoryLabels.length === 0) {
      setVisibleCategoriesCount(categoryLabels.length);
      setShowMoreIndicator(false);
      return;
    }

    const container = chipsContainerRef.current;
    let containerWidth = container.offsetWidth;
    
    // Sur mobile, prendre en compte la largeur du logo Instagram à droite
    if (isMobile && rightColumnMobileRef.current) {
      const logoElement = rightColumnMobileRef.current;
      const logoWidth = logoElement.offsetWidth;
      const logoRight = window.getComputedStyle(logoElement).right;
      const logoRightValue = parseInt(logoRight, 10) || 16; // 1rem = 16px par défaut
      // Largeur totale occupée par le logo: largeur du logo + position right + marge de sécurité
      const instagramLogoTotalWidth = logoWidth + logoRightValue + 8; // 8px de marge de sécurité
      containerWidth = containerWidth - instagramLogoTotalWidth;
    } else if (isMobile) {
      // Si le logo n'est pas encore rendu, utiliser une estimation
      // Le logo Instagram est positionné en absolu à droite avec right: 1rem (16px)
      // Largeur du logo: 40px (max-width: 40px, max-height: 40px)
      // Padding du bouton: 4px de chaque côté = 8px
      // Total: 40px + 16px (right) + 8px (padding) = 64px environ
      const instagramLogoWidth = 64; // Estimation
      containerWidth = containerWidth - instagramLogoWidth;
    } else if (!isMobile && rightColumnDesktopRef.current) {
      // Sur desktop, prendre en compte la largeur de la colonne de droite avec l'icône de contact
      const rightColumnElement = rightColumnDesktopRef.current;
      const rightColumnWidth = rightColumnElement.offsetWidth;
      containerWidth = containerWidth - rightColumnWidth;
    }
    
    if (containerWidth <= 0) {
      // Largeur invalide, réessayer plus tard
      const timeout = setTimeout(() => {
        if (chipsContainerRef.current) {
          let retryWidth = chipsContainerRef.current.offsetWidth;
          if (isMobile && rightColumnMobileRef.current) {
            const logoElement = rightColumnMobileRef.current;
            const logoWidth = logoElement.offsetWidth;
            const logoRight = window.getComputedStyle(logoElement).right;
            const logoRightValue = parseInt(logoRight, 10) || 16;
            retryWidth = retryWidth - (logoWidth + logoRightValue + 8);
          } else if (isMobile) {
            retryWidth = retryWidth - 64; // Estimation
          } else if (!isMobile && rightColumnDesktopRef.current) {
            const rightColumnElement = rightColumnDesktopRef.current;
            const rightColumnWidth = rightColumnElement.offsetWidth;
            retryWidth = retryWidth - rightColumnWidth;
          }
          if (retryWidth > 0) {
            calculateVisibleCategories(retryWidth);
          }
        }
      }, 100);
      return () => clearTimeout(timeout);
    }

    calculateVisibleCategories(containerWidth);

    function calculateVisibleCategories(width) {
      // Sur desktop, limiter à 4 bulles maximum
      if (!isMobile) {
        const count = Math.min(categoryLabels.length, 4);
        setVisibleCategoriesCount(count);
        setShowMoreIndicator(categoryLabels.length > 4);
        return;
      }

      // Sur mobile, calculer dynamiquement selon la largeur disponible
      // Créer un élément temporaire pour mesurer
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.visibility = 'hidden';
      tempContainer.style.display = 'flex';
      tempContainer.style.gap = '0.5rem';
      tempContainer.style.alignItems = 'center';
      tempContainer.style.fontSize = window.getComputedStyle(container).fontSize;
      document.body.appendChild(tempContainer);

      let totalWidth = 0;
      let count = 0;
      const gap = 8; // 0.5rem ≈ 8px

      // Mesurer chaque catégorie
      for (let i = 0; i < categoryLabels.length; i++) {
        const chip = document.createElement('span');
        chip.textContent = categoryLabels[i];
        chip.style.padding = '6px 10px';
        chip.style.borderRadius = '999px';
        chip.style.fontSize = 'clamp(12px, 1.2vw, 13px)';
        chip.style.whiteSpace = 'nowrap';
        chip.style.flexShrink = '0';
        tempContainer.appendChild(chip);
        
        const chipWidth = chip.offsetWidth;
        
        // Si c'est la dernière catégorie, vérifier si on peut l'afficher sans "+X"
        if (i === categoryLabels.length - 1) {
          if (totalWidth + chipWidth <= width) {
            // Toutes les catégories tiennent, pas besoin de "+X"
            count++;
            setShowMoreIndicator(false);
          } else {
            // La dernière ne tient pas, afficher "+X"
            setShowMoreIndicator(true);
          }
        } else {
          // Vérifier si on peut ajouter cette bulle + la bulle "+X"
          const remainingCount = categoryLabels.length - i - 1;
          if (remainingCount > 0) {
            const moreChip = document.createElement('span');
            moreChip.textContent = `+${remainingCount}`;
            moreChip.style.padding = '6px 10px';
            moreChip.style.borderRadius = '999px';
            moreChip.style.fontSize = 'clamp(12px, 1.2vw, 13px)';
            moreChip.style.whiteSpace = 'nowrap';
            moreChip.style.flexShrink = '0';
            tempContainer.appendChild(moreChip);
            const moreChipWidth = moreChip.offsetWidth;
            tempContainer.removeChild(moreChip);
            
            if (totalWidth + chipWidth + gap + moreChipWidth <= width) {
              // Cette bulle + "+X" tiennent, on peut l'afficher
              totalWidth += chipWidth + gap;
              count++;
            } else {
              // Cette bulle ferait dépasser avec "+X", arrêter ici
              setShowMoreIndicator(true);
              break;
            }
          } else {
            // Pas de catégories restantes, on peut afficher cette dernière
            totalWidth += chipWidth + gap;
            count++;
            setShowMoreIndicator(false);
          }
        }
      }

      document.body.removeChild(tempContainer);
      setVisibleCategoriesCount(count);
    }
  }, [categoryLabels, isMobile]);

  const displayedCategories = categoryLabels.slice(0, visibleCategoriesCount);
  const remainingCategoriesCount = categoryLabels.length - visibleCategoriesCount;
  const travelCapabilities = [
    { key: 'home', enabled: canTravel, label: 'à domicile' },
  ];
  const activeTravelCapabilities = travelCapabilities.filter((cap) => cap.enabled);
  const travelExclusive = activeTravelCapabilities.length === 1;
  const hasKidsServices = stylist.hasKidsServices ?? !!stylist.kidsAccepted;

  // Récupérer tous les contacts disponibles (support contact.xxx et xxx direct)
  const getContactValue = (field) => {
    return contact?.[field] || stylist[field] || '';
  };

  const primaryContactKey = contact?.primaryContact || stylist.primaryContact || '';
  
  // Par défaut, utiliser Instagram si primaryContact n'est pas défini ou est vide
  const effectivePrimaryContactKey = primaryContactKey && primaryContactKey.trim() 
    ? primaryContactKey 
    : 'instagram';

  const allContacts = [
    {
      key: 'instagram',
      value: getContactValue('instagram'),
      icon: instagramIcon,
      label: 'Instagram',
      getUrl: (val) => val.startsWith('http') ? val : `https://instagram.com/${val.replace('@', '')}`,
    },
    {
      key: 'whatsapp',
      value: getContactValue('whatsapp'),
      icon: whatsappIcon,
      label: 'WhatsApp',
      getUrl: (val) => val.startsWith('http') ? val : `https://wa.me/${val.replace(/[^0-9]/g, '')}`,
    },
    {
      key: 'phone',
      value: getContactValue('phone'),
      icon: phoneIcon,
      label: 'Téléphone',
      getUrl: (val) => `tel:${val.replace(/[^0-9+]/g, '')}`,
    },
    {
      key: 'tiktok',
      value: getContactValue('tiktok'),
      icon: tiktokIcon,
      label: 'TikTok',
      getUrl: (val) => val.startsWith('http') ? val : `https://tiktok.com/@${val.replace('@', '')}`,
    },
    {
      key: 'externalLink',
      value: getContactValue('externalLink'),
      icon: linkIcon,
      label: 'Lien externe',
      getUrl: (val) => val.startsWith('http') ? val : `https://${val}`,
    },
  ];

  // Afficher uniquement le contact primaire (ou Instagram par défaut)
  const primaryContact = allContacts.find(c => 
    c.key === effectivePrimaryContactKey && 
    getContactValue(effectivePrimaryContactKey)?.trim()
  ) || null;

  const isPro = accountType === 'PRO';
  const isFree = accountType === 'FREE';

  const cardClassName = `${styles.card} ${isFree ? styles.cardFree : ''} ${isDragged ? styles.dragging : ''} ${isDragOver ? styles.dragOver : ''}`;

  return (
    <article 
      className={cardClassName}
      onClick={handleCardClick}
      draggable={isDraggable}
      onDragStart={(e) => onDragStart && onDragStart(e, dragIndex)}
      onDragOver={(e) => onDragOver && onDragOver(e, dragIndex)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop && onDrop(e, dragIndex)}
      onDragEnd={onDragEnd}
    >
      {/* Cœur favori en haut à droite */}
      <button
        type="button"
        className={styles.favoriteButton}
        onClick={handleFavoriteClick}
        aria-label={isFavorite(id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
      >
        {isFavorite(id) ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="black" stroke="none">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
        )}
      </button>
      {/* Row principale horizontale */}
      <div className={styles.cardRow}>
        {/* Colonne gauche : Photo + Nom + Localisation */}
        <div className={styles.profileColumn}>
          <div className={styles.avatarWrapper}>
            <img src={avatarSrc} alt={name} />
          </div>
          <div className={styles.profileInfo}>
            <div className={styles.nameRow}>
              <div className={styles.name}>
                {name}
                {isPro && (
                  <svg className={styles.verifiedBadge} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10M12 2L15.09 5.26L19.18 5.63L18.81 9.72L22 12.77L18.81 15.82L19.18 19.91L15.09 20.28L12 23.5L8.91 20.28L4.82 19.91L5.19 15.82L2 12.77L5.19 9.72L4.82 5.63L8.91 5.26L12 2Z" fill="#B19CD9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              {galleryPhotos && galleryPhotos.length > 0 && !isPro && (
                <>
                  <span className={`${styles.photosBadge} ${styles.photosBadgeDesktop}`}>
                    {t('card.photosAvailable')}
                  </span>
                  <span className={`${styles.photosBadge} ${styles.photosBadgeMobile}`}>
                    Photos disponibles
                  </span>
                </>
              )}
              {isAdminUser && (
                <button
                  type="button"
                  className={styles.deleteButton}
                  onClick={handleDelete}
                  disabled={deleting}
                  aria-label="Supprimer le profil"
                  title="Supprimer le profil"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" />
                  </svg>
                </button>
              )}
            </div>
            <div className={styles.city}>
              {(() => {
                const displayCountry = getCountry(stylist) || country;
                return displayCountry && <span className={styles.countryFlag}>{getCountryFlag(displayCountry)}</span>;
              })()}
              {getDisplayLocation(stylist)}
              {canTravel && <span className={styles.cityTravelNote}> {t('card.canTravel')}</span>}
            </div>
            {reviewCount > 0 && (
              <div className={styles.rating}>
                <StarRating rating={averageRating} reviewCount={reviewCount} size="small" />
              </div>
            )}
          </div>
        </div>

        {/* Colonne centre : Services + Badges + Description + Voir plus */}
        <div className={styles.contentColumn}>
          {/* Services tags */}
          <div className={styles.chipsContainer} ref={chipsContainerRef}>
            {displayedCategories.map((label) => (
              <span key={label} className={styles.chip}>
                {label}
              </span>
            ))}
            {showMoreIndicator && remainingCategoriesCount > 0 && (
              <span className={styles.chip}>
                +{remainingCategoriesCount}
              </span>
            )}
          </div>

          {/* Badges supplémentaires - juste en dessous des services */}
          <div className={styles.actions}>
            {activeTravelCapabilities.map((cap) => (
              <span key={cap.key} className={styles.actionBadge}>
                {travelExclusive ? `uniquement ${cap.label}` : cap.label}
              </span>
            ))}
            {hasKidsServices && <span className={styles.actionBadge}>Coiffe les enfants</span>}
          </div>

          {/* Description */}
          {description && <p className={styles.description}>{description}</p>}

          {/* Galerie de photos - miniatures (desktop uniquement) */}
          {galleryPhotos && galleryPhotos.length > 0 && (
            <div className={`${styles.galleryRow} ${styles.galleryRowDesktop}`}>
              {galleryPhotos.slice(0, 6).map((photo, index) => (
                <div key={index} className={styles.galleryThumbnail}>
                  <img src={photo} alt={`${index + 1}`} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Colonne droite : Séparateur + Contact primaire uniquement (desktop) */}
        <div className={`${styles.rightColumn} ${styles.rightColumnDesktop}`} ref={rightColumnDesktopRef}>
          <div className={styles.separator}></div>
          {primaryContact && (
            <div className={styles.contactsContainer}>
            <a
                href={primaryContact.getUrl(primaryContact.value)}
              target="_blank"
              rel="noopener noreferrer"
                className={styles.contactButton}
                aria-label={primaryContact.label}
              onClick={(e) => {
                e.stopPropagation();
                logContactClick(id, primaryContact.key, currentUser?.uid || null);
              }}
            >
                <img src={primaryContact.icon} alt={primaryContact.label} />
            </a>
            </div>
          )}
        </div>
      </div>
      
      {/* Icône de réseau social (mobile uniquement, centrée dans la hauteur sans la galerie) */}
      {primaryContact && (
        <div className={`${styles.rightColumn} ${styles.rightColumnMobile}`} ref={rightColumnMobileRef}>
          <a
            href={primaryContact.getUrl(primaryContact.value)}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.contactButton}
            aria-label={primaryContact.label}
            onClick={(e) => {
              e.stopPropagation();
              logContactClick(id, primaryContact.key, currentUser?.uid || null);
            }}
          >
            <img src={primaryContact.icon} alt={primaryContact.label} />
          </a>
        </div>
      )}

      {/* Galerie de photos de travail pour les comptes PRO */}
      {isPro && galleryPhotos && galleryPhotos.length > 0 && (
        <div className={styles.proGallery}>
          {galleryPhotos.slice(0, 4).map((photo, index) => (
            <div key={index} className={styles.proGalleryItem}>
              <img 
                src={photo} 
                alt={`Travail ${index + 1} de ${name}`}
                loading="lazy"
                decoding="async"
              />
            </div>
          ))}
        </div>
      )}

      {showAuthModal && createPortal(
        <AuthModal onClose={() => setShowAuthModal(false)} />,
        document.body
      )}
    </article>
  );
}

export default HairstylistCard;
